import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { Steps } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import set from 'lodash/set';
import get from 'lodash/get';
import { useWindowSize } from '../../hooks/useWindowSize';
import './EllipsisSteps.less';

let template = [];

const ellipsisTemplate = {
  icon: (
    <p className="stepsItemIcon">
      <EllipsisOutlined />
    </p>
  ),
};

const createTemplate = (data, length, limit) => {
  if (length <= limit) {
    template = data;
    return;
  }

  const middleIndex = Math.ceil(length / 2) - 1;

  set(template, 0, {
    ...data[0],
    stepIndex: 1,
    icon: <p className="stepsItemIcon">1</p>,
  });
  set(template, middleIndex, {
    stepIndex: middleIndex + 1,
    ...ellipsisTemplate,
  });
  set(template, length - 1, {
    ...data[length - 1],
    stepIndex: length,
    icon: <p className="stepsItemIcon">{length}</p>,
  });
};

export type EllipsisStepsProps = {
  currentStep?: number;
  steps?: Array<Object>;
  limit?: number;
  handelNext: (index: number) => void;
  renderTitle: (section: any) => string | ReactNode;
  onClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const EllipsisSteps: React.FC<EllipsisStepsProps> = ({
  currentStep,
  steps = [],
  limit = 3,
  handelNext = () => {},
  renderTitle = (value) => value.name || '',
}) => {
  const middleIndex = Math.ceil(steps.length / 2) - 1;
  const [stepsForRender, setSteps] = useState(template.filter(Boolean));
  const [lastStep, setLastStep] = useState(Math.max(currentStep - 1 || 0, 0));

  const size = useWindowSize();
  const isMobile = size.width < 426;

  useEffect(() => {
    if (!isEmpty(template)) {
      return;
    }
    createTemplate(steps, steps.length, limit);
  }, [size]);

  const onNext = useCallback(
    (current) => {
      let index = get(stepsForRender, `${current}.stepIndex`);
      if (!index) {
        index = current + 1;
      }
      handelNext(index);
    },
    [stepsForRender],
  );

  useEffect(() => {
    const res = cloneDeep(template);

    if (![0, middleIndex, steps.length - 1].includes(lastStep)) {
      res[lastStep] = undefined;
    } else if (lastStep === middleIndex && steps.length > limit) {
      res[lastStep] = { stepIndex: middleIndex + 1, ...ellipsisTemplate };
    }

    res[currentStep] = {
      ...steps[currentStep],
      stepIndex: currentStep + 1,
      icon: <p className="activeStepsItemIcon">{currentStep + 1}</p>,
    };

    setLastStep(currentStep);
    setSteps(res.filter(Boolean));
  }, [currentStep, steps]);

  return (
    <Steps
      current={currentStep}
      onChange={(current) => {
        onNext(current);
      }}
      direction={isMobile ? 'vertical' : 'horizontal'}
    >
      {stepsForRender.map((section, index) => (
        <Steps.Step
          key={`section-${section.id || index}`}
          title={renderTitle(section)}
          icon={section.icon}
        />
      ))}
    </Steps>
  );
};

export default EllipsisSteps;
